import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HStack, Grid, GridItem } from '@chakra-ui/react';
import {
  selectActiveMarket,
  selectMarketsTimePeriod,
  selectMarketSupplyChainControlState,
  selectSupplyChainHoveredLocation,
  selectSupplyChainIncidents,
} from '../../../../slices/markets/selectors/selectBasicMarketData';
import { updateSupplyChainHoveredLocation, updateSupplyChainViewMode } from '../../../../slices/markets/marketReducers';
import {
  selectMarketSupplyChainData,
  selectMarketSupplyChainGrinders,
  selectMarketSupplyChainPackerPlants,
  selectMarketSupplyChainVolumeData,
  selectSupplyChainDetailsActiveGrinder,
  selectSupplyChainDetailsActivePackerPlant,
} from '../../../../slices/markets/selectors/selectMarketsSupplyChainData';

import SupplyChainIncidents from '../../markets/SupplyChainIncidents/SupplyChainIncidents';
import SupplyChainMap from '../../markets/SupplyChainMap/SupplyChainMap';
import DataTable from '../DataTable/DataTable';
import SankeyChart from '../SankeyChart/SankeyChart';
import VolumeChart from '../VolumeChart/VolumeChart';
import { RadioGroup, Radio } from '@/components/ui/radio';

// This container specifically serves the supply chain card.

const getEntitiesArray = array =>
  array.map((x, i) => {
    return { label: x, isShown: i < 5 };
  });

export default function Details() {
  const dispatch = useDispatch();
  const activeMarket = useSelector(selectActiveMarket);
  const timePeriod = useSelector(selectMarketsTimePeriod);
  const marketSupplyChainData = useSelector(selectMarketSupplyChainData);
  const marketSupplyChainVolumeData = useSelector(selectMarketSupplyChainVolumeData);
  const marketSupplyChainControlState = useSelector(selectMarketSupplyChainControlState);
  const incidents = useSelector(selectSupplyChainIncidents);
  const hoveredLocationId = useSelector(selectSupplyChainHoveredLocation);
  const packer_plant = useSelector(selectSupplyChainDetailsActivePackerPlant);
  const packer_plants = useSelector(selectMarketSupplyChainPackerPlants);
  const grinder = useSelector(selectSupplyChainDetailsActiveGrinder);
  const grinders = useSelector(selectMarketSupplyChainGrinders);

  const handleHoveredLocationChange = React.useCallback(
    id => dispatch(updateSupplyChainHoveredLocation(id)),
    [dispatch]
  );

  const handleViewModeChange = React.useCallback(v => dispatch(updateSupplyChainViewMode(v)), [dispatch]);

  const [viewMode, setViewMode] = React.useState('map');

  return (
    <Grid templateColumns="repeat(24, 1fr)" gap={4}>
      <GridItem colSpan={24}>
        <strong>View</strong>
        <RadioGroup style={{ marginLeft: 10 }} value={viewMode} size="small" onValueChange={e => setViewMode(e.value)}>
          <HStack alignItems="center" justifyContent="end">
            <Radio value="map">Map</Radio>
            <Radio value="relationships">Relationships</Radio>
          </HStack>
        </RadioGroup>
      </GridItem>
      <GridItem colSpan={14}>
        {viewMode === 'map' ? (
          <>
            <SupplyChainMap
              marketSupplyChainData={marketSupplyChainData}
              onHoveredLocationChange={handleHoveredLocationChange}
              controlState={marketSupplyChainControlState}
              hoveredLocationId={hoveredLocationId}
              activeMarket={activeMarket}
              isLarge={!(packer_plant || grinder)}
              incidents={incidents}
            />
            {incidents && incidents.length && <SupplyChainIncidents incidents={incidents} />}
          </>
        ) : (
          <SankeyChart
            marketSupplyChainData={marketSupplyChainData}
            activeMarket={activeMarket}
            isLarge={!(packer_plant || grinder)}
          />
        )}
      </GridItem>
      <GridItem colSpan={10}>
        <DataTable
          marketSupplyChainData={marketSupplyChainData}
          onViewModeChange={handleViewModeChange}
          onHoveredLocationChange={handleHoveredLocationChange}
          controlState={marketSupplyChainControlState}
          hoveredLocationId={hoveredLocationId}
          activeMarket={activeMarket}
          incidents={incidents}
        />
      </GridItem>
      {(packer_plant || grinder) && (
        <GridItem colSpan={24}>
          <VolumeChart
            marketSupplyChainVolumeData={marketSupplyChainVolumeData}
            timePeriod={timePeriod}
            entityList={
              packer_plant !== null
                ? { type: 'grinders', entities: getEntitiesArray(grinders) }
                : grinder !== null
                ? { type: 'establishments', entities: getEntitiesArray(packer_plants) }
                : { type: 'none', entities: [] }
            }
            activeMarket={activeMarket}
          />
        </GridItem>
      )}
    </Grid>
  );
}
